import { useEffect, useRef } from 'react';
import { capturePaymentError } from './paymentErrorHandler';

interface PaypalRenderGateCheck {
  value?: string | null;
  reason: string;
}

interface PaypalRenderGateParams {
  checks: PaypalRenderGateCheck[];
  settleDelayMs?: number;
  extra?: Record<string, any>;
}

/**
 * Watches the same data a PayPal button needs to be usable at checkout
 * (client id / plan id / email). If the user has settled on checkout and
 * one of these is still missing, reports the exact reason to Sentry once —
 * previously these "silent" cases never reached error tracking at all.
 */
export function usePaypalRenderGate({ checks, settleDelayMs = 4000, extra }: PaypalRenderGateParams) {
  const reportedRef = useRef(false);
  const checksKey = checks.map((check) => `${check.reason}:${check.value ?? ''}`).join('|');

  useEffect(() => {
    if (reportedRef.current) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (reportedRef.current) {
        return;
      }

      const failedCheck = checks.find((check) => !check.value);
      if (!failedCheck) {
        return;
      }

      reportedRef.current = true;
      capturePaymentError({
        provider: 'paypal',
        stage: 'render_gate',
        errorName: 'PayPalRenderGateError',
        message: `PayPal button not usable at checkout: ${failedCheck.reason}`,
        extra: { reason: failedCheck.reason, ...extra },
      });
    }, settleDelayMs);

    return () => clearTimeout(timeoutId);
  }, [checksKey]);
}
